import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 mb-6"><Logo size={40} /><span className="font-serif text-2xl text-olive">L’Orne Park</span></Link>
        <h1 className="font-serif text-3xl text-olive">{title}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
        <div className="mt-6 bg-white rounded-2xl border border-border p-6">{children}</div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Mot de passe oublié" subtitle="Indiquez votre email, nous vous envoyons un lien de réinitialisation valable 1 heure.">
      {sent ? (
        <p className="text-sm text-olive" data-testid="forgot-sent">Si un compte existe avec <strong>{email}</strong>, un email vient d'être envoyé. Pensez à vérifier vos spams.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="forgot-email" /></div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="forgot-submit">{busy ? "..." : "Envoyer le lien"}</Button>
        </form>
      )}
      <p className="text-sm text-center text-muted-foreground mt-5"><Link to="/connexion" className="text-rose-brand font-semibold">Retour à la connexion</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token: params.get("token"), new_password: pwd });
      setUser(data.user); toast.success("Mot de passe réinitialisé, vous êtes connecté");
      navigate(data.user.role === "traveler" ? "/mes-sejours" : "/espace-hote");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Nouveau mot de passe" subtitle="Choisissez un mot de passe d'au moins 8 caractères.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div><Label>Nouveau mot de passe</Label><Input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1 h-11" data-testid="reset-password" /></div>
        <div><Label>Confirmer</Label><Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-11" data-testid="reset-confirm" /></div>
        <Button type="submit" disabled={busy || !params.get("token")} className="w-full h-11 bg-olive text-white" data-testid="reset-submit">{busy ? "..." : "Enregistrer et me connecter"}</Button>
      </form>
    </Shell>
  );
}
